import { getAppEnvironment, type AppEnvironment } from '@/lib/appEnvironment';
import { GP_MEMBERSHIP_BOOKING_FLAG_KEY } from '@/lib/membershipFlags';

/**
 * Registry of environment-scoped feature flags (release toggles).
 *
 * Each entry declares in which deploy environments the feature is ON.
 * Everything not listed here is OFF everywhere. Development deployments use
 * the Staging runtime configuration; Development is not a flag environment.
 *
 * Conventions (from the project flag guide):
 * - keys are `<category>.<slug>`, lowercase, immutable once shipped
 * - default is OFF; features opt IN per environment — never opt out
 * - delete the entry (and its code branch) once fully rolled out
 */
export interface FeatureFlagDefinition {
  /** Human-readable description shown in the admin flags readout. */
  description: string;
  /** Environments where the feature is ON. */
  environments: readonly AppEnvironment[];
  /** Optional expiry — expired flags evaluate OFF and get reported. */
  expiresAt?: string;
}

/**
 * Flag key for the CCSD/BUPA code search behaviour (environment-scoped).
 * Kept here as the single immutable source of truth, re-exported by the
 * search gate (`@/features/search/data/ccsdSearchGate`) for consumers.
 */
export const CCSD_SEARCH_FLAG_KEY = 'component.global.ccsd-code-search';
const FEATURE_FLAGS = {
  // CCSD/BUPA code search (consultants-only dropdown). The non-production
  // sites share Staging configuration.
  [CCSD_SEARCH_FLAG_KEY]: {
    description: 'CCSD/BUPA code search with consultants-only dropdown',
    environments: ['staging', 'production'],
  },
  // Cloudflare manages the per-environment value; the server gate remains
  // authoritative and keeps production disabled.
  [GP_MEMBERSHIP_BOOKING_FLAG_KEY]: {
    description: 'GP Membership booking availability',
    environments: ['staging'],
  },
} as const satisfies Record<string, FeatureFlagDefinition>;

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

/** All registered flag keys (for the admin readout and tests). */
export function getFeatureFlagKeys(): FeatureFlagKey[] {
  return Object.keys(FEATURE_FLAGS) as FeatureFlagKey[];
}

/** Registry entry for a flag key, or undefined for unregistered keys. */
export function getFeatureFlagDefinition(
  key: string,
): FeatureFlagDefinition | undefined {
  return (FEATURE_FLAGS as Record<string, FeatureFlagDefinition>)[key];
}

/**
 * Whether a flag entry is expired. Expired flags evaluate OFF.
 * Invalid `expiresAt` values never expire (fail open by default-off anyway).
 */
export function isFeatureFlagExpired(
  definition: FeatureFlagDefinition,
): boolean {
  if (!definition.expiresAt) return false;
  const expiresAt = Date.parse(definition.expiresAt);
  if (Number.isNaN(expiresAt)) return false;
  return Date.now() >= expiresAt;
}

/**
 * Core check: is the flag ON in the given environment?
 * Unknown keys and expired flags are always OFF.
 */
export function isFeatureFlagEnabledIn(
  key: string,
  environment: AppEnvironment,
): boolean {
  const definition = getFeatureFlagDefinition(key);
  if (!definition) return false;
  if (isFeatureFlagExpired(definition)) return false;
  return definition.environments.includes(environment);
}

/**
 * Convenience check against the current runtime environment.
 * Server reads APP_ENV; client reads build-time NEXT_PUBLIC_APP_ENV so the
 * production artifact (promoted staging → production) resolves correctly
 * per deploy without a rebuild.
 */
export function isFeatureFlagEnabled(key: string): boolean {
  return isFeatureFlagEnabledIn(key, getAppEnvironment());
}
